/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useEffect, useState } from 'react';
import { ErrorCode, IConfigEntry, IProcessPaymentWithManifestRequest, PaymentType } from '@ace-clublabs/payment-types';
import {
  AppHeaderBackButton,
  Typography,
  Box,
  TextField,
  Icon,
  Link,
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  ModalActionButton,
  ModalCloseIconButton,
  useNameOnCardValidator,
  Alert,
  AlertTypography,
  StickyButton,
} from '@ace-clublabs/shared-component-library';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import { PaymentReview } from '../containers/PaymentReview';
import { useGlobalState } from '../shared/state';
import PaymentHeader from './PaymentHeader';
import { PaymentMethodsAuthorizationModal } from './PaymentMethodsAuthorizationModal';
import { Pages } from '../shared/pages';
import { ICardFormProps, IFPCreditCardFormValidation } from '../types';
import { UtagHelper } from '../utils/utagHelper';
import { ProcessPaymentHelper } from '../utils/processPaymentHelper';
import { ErrorSeverity, ErrorType } from '../shared/types';
import UrlHelper from '../utils/urlHelper';
import { allowStoredPayment } from '@lib/shared';
import LogCannon from '@ace-clublabs/log-cannon';
import mastercardLogo from '../assets/mastercardFreedompay.svg';
import amexLogo from '../assets/americanExpressFreedompay.svg';
import discoverLogo from '../assets/discoverFreedompay.svg';
import visaLogo from '../assets/visaFreedompay.svg';

const CardHPC: React.FC<ICardFormProps> = (props) => {
  const navigate = useNavigate();

  const [formDataValidation, setFormDataValidation] = useState<IFPCreditCardFormValidation>({
    nameOnTheCard: false,
    cardNumber: false,
    expirationDate: false,
    securityCode: false,
    postalCode: false,
  });
  const [formValid, setFormValid] = useState(false);
  const [nameOnCard, setNameOnCard] = useState('');
  const [fpPaymentKeys, setfpPaymentKeys] = useState('');
  const [fpResponse, setfpResponse] = useState<{ Key: string; Value: string }[]>();
  const [storePaymentChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [_paymentType] = useGlobalState('paymentType');
  const [isProcessing, setIsProcessing] = useState(true);
  const [isPaymentReview, setPaymentReview] = useState(false);
  const [, setGlobalStateError] = useGlobalState('error');
  const [error, setError] = useState('');
  const [manifest] = useGlobalState('manifest');
  const [showPaymentAuthorization, setShowPaymentAuthorization] = useState(false);
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [paymentMethodsSkipped, setpaymentMethodsSkipped] = useGlobalState('paymentMethodsSkipped');
  const [, setPaymentProcessing] = useGlobalState('paymentProcessing');

  const {
    manifestId,
    paymentType,
    amount,
    returnUrl,
    autoPay,
    // tokenOnly,
    tags,
    appId,
    softErrorCode,
    scheduledDate,
    isScheduled,
    paymentFormSubtitle,
    fpHost,
    fpSessionKey,
    setParentProcessing,
    isUnitTest,
  } = props;

  const config = manifest?.clientConfig || ({} as IConfigEntry);
  const disableCVV = config?.disableCVV;

  useEffect(() => {
    if (softErrorCode) {
      handleSoftError({ code: softErrorCode });
    }
  }, []);

  useEffect(() => {
    setParentProcessing({ status: true, text: 'Your checkout is loading', cindy: true, displayCheckmark: false });
    (async () => {
      try {
        setFormDataValidation({ ...formDataValidation, securityCode: disableCVV });
      } catch (error) {
        const errorMessage = 'ERROR: Set Form Data Validation Exception';
        if (typeof error === 'string') {
          LogCannon.blast(`${errorMessage}: ${error}`);
        } else {
          LogCannon.blast(errorMessage, error as object);
        }
        navigate(Pages.Error);
      }
    })();
  }, [manifestId]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [fpHost, formDataValidation]);

  useEffect(() => {
    setFormValid(Object.values(formDataValidation).every((value) => value === true));
  }, [formDataValidation]);

  const handleMessage = (event: MessageEvent) => {
    const eventData = event.data;
    const data = eventData.data;

    switch (eventData.type) {
      case 1:
        const error = {
          errorMessage: 'This session has expired or cannot be located. Please try again',
          errorType: ErrorType.SESSION_TIMEOUT,
        };
        setGlobalStateError(error);
        const errorMessage = 'ERROR: HPC Session Expired';
        LogCannon.blast(errorMessage, error as object);
        navigate(Pages.Error);
        break;
      case 2: {
        const fp_iframe = document.getElementById('hpc--card-frame') as HTMLIFrameElement;
        fp_iframe.style.height = data.height + 'px';
        break;
      }
      case 3: {
        const nameOnCardField = document.querySelector('#name-on-card') as HTMLInputElement;
        if (nameOnCardField) {
          nameOnCardField.classList.remove('disabled');
        }
        setfpPaymentKeys(data.paymentKeys);
        const fpResponse: { Key: string; Value: string }[] = data.attributes;

        setfpResponse(fpResponse);
        handleSubmit(fpResponse, data.paymentKeys);
        break;
      }
      case 4: {
        const targetKey = data.emittedBy[0].toLowerCase() + data.emittedBy.substring(1);
        setFormDataValidation({ ...formDataValidation, [targetKey]: data.isValid });
        break;
      }
    }
  };

  const handleFpFormSubmit = async (e: any) => {
    // submit fp form first
    const iframe = document.querySelector<HTMLIFrameElement>('#hpc--card-frame');

    if (iframe?.contentWindow) {
      const urlObj = new URL(fpHost ?? '');
      const baseUrl = `${urlObj.protocol}//${urlObj.host}`;
      const nameOnCardField = document.querySelector('#name-on-card') as HTMLInputElement;
      if (nameOnCardField) {
        nameOnCardField.classList.add('disabled');
      }
      iframe.contentWindow.postMessage('getPaymentKeys', baseUrl);
    }
  };

  const renderPaymentReview = manifest?.clientConfig?.renderPaymentReview;

  const handleSubmit = async (fpResponse: any, fpPaymentKeys: string) => {
    if (renderPaymentReview && amount > 0) {
      setPaymentReview(true);
    } else {
      await submitPayment(fpResponse, fpPaymentKeys);
    }
  };

  const handleSoftError = (error: { code: ErrorCode }) => {
    if (tags) {
      const paymentTags = UtagHelper.tagPage(tags, 'payment');
      paymentTags['error'] = `blocked payment-${error.code}`;
      UtagHelper.link(paymentTags);
    }
    switch (error.code) {
      case ErrorCode.InvalidAccountHolderName:
        setError('Invalid name on the card.');
        break;
      case ErrorCode.InvalidExpirationDate:
        setError('Invalid expiration date.');
        break;
      case ErrorCode.InvalidCard:
      case ErrorCode.InvalidToken:
      case ErrorCode.InvalidInsuranceAutopayPaymentMethod:
      case ErrorCode.CardTypeNotAllowed:
      case ErrorCode.InvalidAccountNumber:
        setError('Invalid card number.');
        break;
      case ErrorCode.StorePaymentError:
        setError('Sorry, your payment method could not be saved for future payments.');
        break;
      case ErrorCode.UnknownCardType:
      case ErrorCode.StandaloneAutoPayError:
      case ErrorCode.InvalidPaymentGatewayToken:
        setError('The card entered is not supported. Please try a different card.');
        break;
      case ErrorCode.ChargeDeclined:
        setError('Charge declined, please use a different card.');
        break;
      case ErrorCode.CreditCardNotAllowed:
        setError('Only debit cards are accepted, please try again.');
        break;
      case ErrorCode.DebitCardNotAllowed:
        setError('Debit cards are not accepted, please try again.');
        break;
      case ErrorCode.PrepaidCardNotAllowedForAutoPay:
      case ErrorCode.PrepaidCardNotAllowed:
        setError('Prepaid cards are not accepted, please try again.');
        break;
      case ErrorCode.CardMustBeAAAVisa:
        setError('Card must be a AAA Visa card.');
        break;
      case ErrorCode.InvalidZipCode:
        setError('Invalid billing ZIP code.');
        break;
      case ErrorCode.InvalidCVV:
        setError('Invalid CVV/security code.');
        break;
      case ErrorCode.InsufficientFunds:
        setError('Insufficient funds, please try again.');
        break;
      case ErrorCode.NetworkTimeout:
        if (appId === 'manage-saved-payments') {
          setError('Your payment method could not be saved. Please try again.');
        } else setShowModal(true);
        break;
      default:
        setShowModal(true);
        break;
    }
  };

  const submitPayment = async (fpAttributes: any, fpPaymentKeys: string) => {
    if (fpAttributes) {
      let fpObj: any = {};
      fpAttributes.map((attribute: { Key: string; Value: string }) => (fpObj[attribute.Key] = attribute.Value));
      const { MaskedCardNumber, CardIssuer, PostalCode, ExpirationDate } = fpObj;
      const processFPPaymentBody = {
        manifestId: manifestId,
        fpData: {
          last4: MaskedCardNumber?.slice(-4),
          cardBrand: CardIssuer,
          cardHolderName: nameOnCard,
          zipCode: PostalCode,
          expMonth: ExpirationDate?.split('/')[0],
          expYear: ExpirationDate?.split('/')[1],
          bin: MaskedCardNumber?.substring(0, 6),
          paymentKey: fpPaymentKeys ? fpPaymentKeys[0] : '',
          sessionKey: fpSessionKey,
        },
        paymentType: pmtType === PaymentType.Undefined ? PaymentType.CreditOrDebit : pmtType,
      } as IProcessPaymentWithManifestRequest;

      if ((allowStoredPayment && storePaymentChecked) || appId === 'manage-saved-payments')
        processFPPaymentBody.storePayment = {
          enable: true,
        };
      setPaymentReview(false);
      setParentProcessing({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: false,
      });
      setIsProcessing(true);
      try {
        setPaymentProcessing(true);
        const { transactionId } = await ProcessPaymentHelper.processPayment(processFPPaymentBody);
        setParentProcessing({
          status: true,
          text: '',
          cindy: false,
          displayCheckmark: true,
          callback: () => {
            const returnUrlWithTransactionId = UrlHelper.addQueryParam(returnUrl, { transactionId });
            window.location.href = returnUrlWithTransactionId;
          },
        });
      } catch (error: any) {
        if (error.type === ErrorSeverity.SOFT) {
          setParentProcessing({
            status: true,
            text: '',
            cindy: false,
            displayCheckmark: true,
            callback: () => {
              setIsProcessing(false);
            },
          });
          handleSoftError(error);
        } else {
          const errorMessage = 'ERROR: Fail to Process HPC Payment';
          if (typeof error === 'string') {
            LogCannon.blast(`${errorMessage}: ${error}`);
          } else {
            LogCannon.blast(errorMessage, error as object);
          }
          if (error.code === ErrorCode.TooManyRequests) {
            setGlobalStateError({ errorType: ErrorType.RATE_LIMITED, errorMessage: '' });
          }
          navigate(Pages.Error);
        }
      }
    }
  };

  const submitPaymentFromReview = async () => {
    try {
      if (fpResponse) {
        await submitPayment(fpResponse, fpPaymentKeys);
      } else {
        const errorMessage = 'ERROR: Submit Payment From Review Missing FP Response';
        LogCannon.blast(errorMessage);
        navigate(Pages.Error);
      }
    } catch (error: any) {
      if (error.type === ErrorSeverity.SOFT) {
        setParentProcessing({
          status: true,
          text: '',
          cindy: false,
          displayCheckmark: true,
          callback: () => {
            setIsProcessing(false);
          },
        });
        setPaymentReview(false);
        handleSoftError(error);
      } else {
        const errorMessage = 'ERROR: Fail to Process HPC Payment From Review';
        if (typeof error === 'string') {
          LogCannon.blast(`${errorMessage}: ${error}`);
        } else {
          LogCannon.blast(errorMessage, error as object);
        }
        if (error.code === ErrorCode.TooManyRequests) {
          setGlobalStateError({ errorType: ErrorType.RATE_LIMITED, errorMessage: '' });
        }
        navigate(Pages.Error);
      }
    }
  };

  const modalDialog = (
    <Modal
      open={showModal}
      onClose={() => setShowModal(false)}
      variants={{
        sm: 'bottom-sheet',
        md: 'regular',
      }}
    >
      <ModalTitle id={`error-modal-title`} sx={{ marginRight: '2rem' }}>
        Please try again.
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowModal(false)} />
      </ModalTitle>

      <ModalContent id="error-modal-content">
        <Typography variant="b1Regular">There was an unexpected error.</Typography>
      </ModalContent>
      <ModalActions
        actionLayout={{
          xs: 'fullWidth',
          sm: 'fullWidth',
          md: 'fullWidth',
          lg: 'rightAlign',
          xl: 'rightAlign',
        }}
      >
        <ModalActionButton label="OK" onClick={() => setShowModal(false)} />
      </ModalActions>
    </Modal>
  );

  const isScheduledPayment: boolean = !!isScheduled || !!manifest?.isScheduled;
  const authText =
    isScheduledPayment || !(amount > 0) || renderPaymentReview ? 'Continue' : 'Pay now';

  const handleBack = () => {
    if (paymentMethodsSkipped) {
      setpaymentMethodsSkipped(false);
      navigate(-2);
    } else navigate(-1);
  };

  const handleNameOnCard = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const target = event.target as HTMLElement;
    if (target instanceof HTMLElement) {
      const input = (target as HTMLInputElement | HTMLTextAreaElement).value.trim();
      setNameOnCard(input);
      const hasNameError = document.querySelector('#name-on-card-helper-text')?.textContent !== '';
      setFormDataValidation({ ...formDataValidation, nameOnTheCard: input.length > 0 && !hasNameError });

      if (input !== '') {
        const ariaInvalid = target.getAttribute('aria-invalid');
        if (ariaInvalid === 'false') {
          target.classList.add('green-outline');
          target.classList.remove('red-outline');
        } else if (ariaInvalid === 'true') {
          target.classList.add('red-outline');
          target.classList.remove('green-outline');
        }
      } else {
        target.classList.add('red-outline');
      }
    }
  };

  const pmtType =
    paymentType && paymentType !== PaymentType.Undefined
      ? paymentType
      : _paymentType && _paymentType !== PaymentType.Undefined
        ? _paymentType
        : PaymentType.Undefined;

  const amountDue = amount > 0;
  const buttonText = !isScheduled && amountDue && !renderPaymentReview ? 'Pay now' : 'Continue';

  return (
    <>
      {isPaymentReview && (
        <PaymentReview
          amount={manifest?.amount!}
          paymentDetails={{
            paymentType: PaymentType.Undefined,
            card: {
              last4: fpResponse ? fpResponse[1].Value.slice(-4) : '',
              brand: fpResponse ? fpResponse[0].Value : '',
            },
          }}
          pit={manifest?.pit ? { ...manifest.pit } : undefined}
          scheduledDate={scheduledDate}
          onSubmit={submitPaymentFromReview}
          onBack={() => setPaymentReview(false)}
        />
      )}

      <Box className={`payment-form ${!isPaymentReview && !isProcessing ? '' : 'shrink'}`} tabIndex={0}>
        <Box className="payment-form-cream-section">
          <Box className="payment-form-body inner-container">
            <Header />
            {!isMobileApp && <AppHeaderBackButton id="back-button" onClick={handleBack} />}
            <Box className="secure-checkout">
              <Icon iconName="lock-keyhole-solid" className="lock-icon" />
              <span>Secure checkout</span>
            </Box>
            <PaymentHeader paymentType={pmtType} amount={amount} autoPay={autoPay} />
            {paymentFormSubtitle ? (
              <Typography variant="b1Regular" data-quid="card-form-subtitle" id="card-form-subtitle">
                {paymentFormSubtitle}
              </Typography>
            ) : (
              <Box id="card-form-subtitle-container">
                {config?.allowedPaymentTypes?.includes(PaymentType.DebitOnly) && (
                  <Box className="card-subtitle" id="payment-method-debit-card">
                    <Typography variant="b1Regular" data-quid="card-form-subtitle">
                      Debit card
                    </Typography>
                    <Box className="card-logo">
                      <img src={mastercardLogo} alt="Mastercard" />
                      <img src={visaLogo} alt="Visa" />
                    </Box>
                  </Box>
                )}
                {config?.allowedPaymentTypes?.includes(PaymentType.CreditOrDebit) && (
                  <Box className="card-subtitle" id="payment-method-credit-card" sx={{ alignContent: 'space-between' }}>
                    <Typography variant="b1Regular" data-quid="card-form-subtitle">
                      Credit card
                    </Typography>
                    <Box className="card-logo">
                      <img src={amexLogo} alt="American Express" />
                      <img src={mastercardLogo} alt="Mastercard" />
                      <img src={visaLogo} alt="Visa" />
                      <img src={discoverLogo} alt="Discover" />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Box className="payment-form-body inner-container">
          <Box
            sx={{
              '& .MuiTextFieldValidationErrorIcon': { display: 'none' },
              '& .MuiTextFieldValidation .MuiTextFieldValidationErrorMessage': { marginLeft: 0 },
              '& .MuiAlert-colorError': { marginBottom: '1rem' },
            }}
          >
            {error && (
              <Alert severity="error" id="soft-error-alert" displayCloseButton={false}>
                <AlertTypography>{error}</AlertTypography>
              </Alert>
            )}
            <TextField
              id="name-on-card"
              label="Name on the Card"
              placeholder="First and Last Name"
              maxLength={26}
              validator={useNameOnCardValidator()}
              onBlur={handleNameOnCard}
              onChange={handleNameOnCard}
              fullWidth
              sx={{
                '& .MuiInputAdornment-root': { display: 'none' },
              }}
            />
          </Box>
          <Box sx={{ margin: '2px -10px -10px', overflow: 'hidden' }}>
            <iframe
              id="hpc--card-frame"
              title="freedom pay payment form"
              src={`${fpHost}?sessionKey=${fpSessionKey}`}
              onLoad={async () => {
                setParentProcessing({
                  status: true,
                  text: 'Your checkout is loading',
                  cindy: true,
                  displayCheckmark: true,
                  callback: () => {
                    setIsProcessing(false);
                  },
                });
              }}
            />
          </Box>
          {!(renderPaymentReview || amount === 0) && (
            <Typography variant="b2Regular" data-quid="payment-authorization-text-card">
              By clicking {authText}, I hereby state that I am an authorized user for the above payment method, and have
              read and agree to the{' '}
              <Link
                id="payment-authorization-link"
                component="button"
                sx={{ verticalAlign: 'baseline' }}
                onClick={() => {
                  setShowPaymentAuthorization(true);
                }}
                variant="b2Regular"
              >
                Payment Authorization
              </Link>
              .
            </Typography>
          )}
        </Box>
        <StickyButton
          id="submit-button"
          slotProps={{
            primaryButton: {
              label: buttonText,
              onClick: handleFpFormSubmit,
              disabled: isUnitTest ? false : !formValid,
              endIcon: amountDue ? <Icon iconName='lock-keyhole-solid' /> : undefined
            },
          }}
          renderAmount={amountDue ? amount : undefined}
        />
        {showPaymentAuthorization && (
          <PaymentMethodsAuthorizationModal
            handleClose={() => {
              setShowPaymentAuthorization(false);
            }}
            ctaText={authText}
          ></PaymentMethodsAuthorizationModal>
        )}
      </Box>
      {modalDialog}
    </>
  );
};

export default CardHPC;
